/* ==========================================================================
   Portal API Functions
   Portal-scoped endpoints for catalog and prescriptions
   ========================================================================== */

import apiClient from './client'
import type {
  PortalConfigResponse,
  PortalCatalogResponse,
  CatalogProductForPortal,
  PrescriptionSubmit,
  PrescriptionSubmitResponse,
  PrescriptionBulkSubmit,
  BulkPrescriptionResponse,
  BulkPrescriptionResult,
  PrescriptionListResponse,
  PatientListResponse,
  ProviderPrescriptionDetail,
  PrescriptionStatus,
  PdfDownloadResponse,
  PublicCatalogResponse,
  AccessRequestCreate,
  PrescriptionDraftSave,
  PrescriptionDraftSaveResponse,
  PrescriptionDraftListResponse,
  PrescriptionDraftDetail,
} from './types'
import { BULK_MAX_LINE_ITEMS_PER_REQUEST } from './types'

/* --------------------------------------------------------------------------
   Catalog Cache
   Session-level cache for catalog products
   -------------------------------------------------------------------------- */

interface CatalogCache {
  [slug: string]: {
    products: CatalogProductForPortal[]
    fetchedAt: number
  }
}

const catalogCache: CatalogCache = {}
const CACHE_TTL_MS = 5 * 60 * 1000 // 5 minutes

/**
 * Check if cached catalog is still valid.
 */
function isCacheValid(slug: string): boolean {
  const cached = catalogCache[slug]
  if (!cached) return false
  return Date.now() - cached.fetchedAt < CACHE_TTL_MS
}

/**
 * Clear the catalog cache for a specific slug or all slugs.
 */
export function clearCatalogCache(slug?: string): void {
  if (slug) {
    delete catalogCache[slug]
  } else {
    Object.keys(catalogCache).forEach((key) => delete catalogCache[key])
  }
}

/* --------------------------------------------------------------------------
   Portal API Functions
   -------------------------------------------------------------------------- */

/**
 * Get portal configuration (branding, pharmacy info).
 */
export async function getPortalConfig(
  slug: string
): Promise<PortalConfigResponse> {
  const response = await apiClient.get<PortalConfigResponse>(`/portal/${slug}`)
  return response.data
}

/**
 * Get catalog products for a portal.
 * Results are cached for the session to reduce API calls.
 *
 * @param slug - The portal slug
 * @param forceRefresh - Bypass cache and fetch fresh data
 */
export async function getCatalog(
  slug: string,
  forceRefresh = false
): Promise<CatalogProductForPortal[]> {
  // Return cached data if valid
  if (!forceRefresh && isCacheValid(slug)) {
    return catalogCache[slug].products
  }

  // Fetch from API
  const response = await apiClient.get<PortalCatalogResponse>(
    `/portal/${slug}/catalog`
  )
  const products = response.data.products

  // Cache the results
  catalogCache[slug] = {
    products,
    fetchedAt: Date.now(),
  }

  return products
}

/**
 * Get recently prescribed products for the authenticated provider.
 * Returns up to 5 products ordered by most recent prescription.
 */
export async function getRecentProducts(
  slug: string
): Promise<CatalogProductForPortal[]> {
  const response = await apiClient.get<CatalogProductForPortal[]>(
    `/portal/${slug}/catalog/recent`
  )
  return response.data
}

/**
 * Submit a prescription.
 */
export async function submitPrescription(
  slug: string,
  data: PrescriptionSubmit
): Promise<PrescriptionSubmitResponse> {
  const response = await apiClient.post<PrescriptionSubmitResponse>(
    `/portal/${slug}/prescriptions`,
    data
  )
  return response.data
}

/**
 * Submit multiple prescriptions for the same patient in one request.
 *
 * Sends a `PrescriptionBulkSubmit` payload: shared patient data, practice
 * address, signature, and a list of up to `BULK_MAX_LINE_ITEMS_PER_REQUEST`
 * product line items. Each line becomes its own prescription, PDF, and fax.
 * Pre-flight gates (auth, profile, subscription, signature requirement, item
 * count) abort the whole request; per-item failures (deactivated product,
 * PDF error, fax error) are returned per-item without aborting.
 *
 * Callers with more items than one request allows should use
 * `submitPrescriptionBulkChunked`.
 */
export async function submitPrescriptionBulk(
  slug: string,
  data: PrescriptionBulkSubmit
): Promise<BulkPrescriptionResponse> {
  const response = await apiClient.post<BulkPrescriptionResponse>(
    `/portal/${slug}/prescriptions/bulk`,
    data
  )
  return response.data
}

/**
 * Thrown by `submitPrescriptionBulkChunked` when at least one chunk completed
 * before a later chunk's request failed. Items in the caller's list fall into
 * three ranges:
 *
 * - `[0, results.length)` — confirmed; `results` holds their server results,
 *   re-indexed to the caller's full list. Must NOT be resubmitted.
 * - `[results.length, results.length + attemptedCount)` — the chunk whose
 *   request failed. Outcome unknown: on a timeout the backend may still have
 *   processed and faxed them, so callers should steer the user to check
 *   history before resubmitting.
 * - `[results.length + attemptedCount, ...)` — never sent.
 *
 * `cause` is the underlying request error.
 */
export class BulkSubmitPartialError extends Error {
  readonly results: BulkPrescriptionResult[]
  readonly attemptedCount: number

  constructor(
    results: BulkPrescriptionResult[],
    attemptedCount: number,
    cause: unknown
  ) {
    super(
      cause instanceof Error
        ? cause.message
        : 'Failed to submit the remaining prescriptions.',
      { cause }
    )
    this.name = 'BulkSubmitPartialError'
    this.results = results
    this.attemptedCount = attemptedCount
  }
}

/**
 * Submit a bulk payload of any size (up to `MAX_PRODUCTS_PER_SUBMISSION`) by
 * splitting it into sequential requests of at most
 * `BULK_MAX_LINE_ITEMS_PER_REQUEST` items (PORTAL-190).
 *
 * Why chunk instead of one big request: the backend processes items
 * sequentially (PDF + S3 upload + fax, ~0.6s typical, several seconds worst
 * case), and nginx cuts the request at 60s while the backend keeps going and
 * faxes everything. One oversized request can therefore show the provider an
 * error for a batch the pharmacy actually received. Keeping every request at
 * the previous 10-item size keeps each one inside the proxy timeout.
 *
 * Chunks are sent one at a time, on purpose: stopping at the first failed
 * request gives a clean "confirmed up to here" boundary, which the form needs
 * to save the remainder as a draft without duplicating anything.
 *
 * Result indices are re-based so they match positions in `data.prescriptions`.
 * If a chunk after the first fails, throws `BulkSubmitPartialError` carrying
 * the results confirmed so far; if the first chunk fails, the original error
 * propagates unchanged (nothing was sent).
 */
export async function submitPrescriptionBulkChunked(
  slug: string,
  data: PrescriptionBulkSubmit
): Promise<BulkPrescriptionResponse> {
  const results: BulkPrescriptionResult[] = []
  const items = data.prescriptions
  const chunkSize = BULK_MAX_LINE_ITEMS_PER_REQUEST

  for (let offset = 0; offset < items.length; offset += chunkSize) {
    const chunk = items.slice(offset, offset + chunkSize)
    try {
      const response = await submitPrescriptionBulk(slug, {
        ...data,
        prescriptions: chunk,
      })
      for (const r of response.results) {
        results.push({ ...r, index: r.index + offset })
      }
    } catch (err) {
      if (offset === 0) throw err
      throw new BulkSubmitPartialError(results, chunk.length, err)
    }
  }

  return { results }
}

/* --------------------------------------------------------------------------
   Prescription Drafts ("save script for later" — PORTAL-61)
   -------------------------------------------------------------------------- */

/**
 * Save the current (possibly incomplete) prescription form as a draft without
 * submitting it. No signature is required and nothing is faxed.
 */
export async function saveDraft(
  slug: string,
  data: PrescriptionDraftSave
): Promise<PrescriptionDraftSaveResponse> {
  const response = await apiClient.post<PrescriptionDraftSaveResponse>(
    `/portal/${slug}/prescriptions/drafts`,
    data
  )
  return response.data
}

/**
 * Overwrite an existing draft with the current form state ("save again").
 */
export async function updateDraft(
  slug: string,
  draftId: string,
  data: PrescriptionDraftSave
): Promise<PrescriptionDraftSaveResponse> {
  const response = await apiClient.put<PrescriptionDraftSaveResponse>(
    `/portal/${slug}/prescriptions/drafts/${draftId}`,
    data
  )
  return response.data
}

/**
 * List the authenticated provider's saved drafts (most recent first).
 */
export async function listDrafts(
  slug: string
): Promise<PrescriptionDraftListResponse> {
  const response = await apiClient.get<PrescriptionDraftListResponse>(
    `/portal/${slug}/prescriptions/drafts`
  )
  return response.data
}

/**
 * Fetch a saved draft's full payload, used to prefill the form.
 */
export async function getDraft(
  slug: string,
  draftId: string
): Promise<PrescriptionDraftDetail> {
  const response = await apiClient.get<PrescriptionDraftDetail>(
    `/portal/${slug}/prescriptions/drafts/${draftId}`
  )
  return response.data
}

/**
 * Delete a saved draft (also called after it is successfully submitted).
 */
export async function deleteDraft(slug: string, draftId: string): Promise<void> {
  await apiClient.delete(`/portal/${slug}/prescriptions/drafts/${draftId}`)
}

/* --------------------------------------------------------------------------
   Provider Prescription History
   -------------------------------------------------------------------------- */

/**
 * List prescriptions for the authenticated provider.
 */
export async function getProviderPrescriptions(
  slug: string,
  options?: {
    status?: PrescriptionStatus
    date_from?: string
    date_to?: string
    search?: string
    sort_by?: string
    sort_order?: string
    limit?: number
    offset?: number
  }
): Promise<PrescriptionListResponse> {
  const params = new URLSearchParams()
  if (options?.status) params.set('status', options.status)
  if (options?.date_from) params.set('date_from', options.date_from)
  if (options?.date_to) params.set('date_to', options.date_to)
  if (options?.search) params.set('search', options.search)
  if (options?.sort_by) params.set('sort_by', options.sort_by)
  if (options?.sort_order) params.set('sort_order', options.sort_order)
  if (options?.limit !== undefined) params.set('limit', String(options.limit))
  if (options?.offset !== undefined) params.set('offset', String(options.offset))

  const query = params.toString()
  const url = `/portal/${slug}/prescriptions${query ? `?${query}` : ''}`
  const response = await apiClient.get<PrescriptionListResponse>(url)
  return response.data
}

/**
 * Get a pre-signed URL for downloading a prescription PDF.
 */
export async function getPrescriptionPdfUrl(
  slug: string,
  prescriptionId: string
): Promise<string> {
  const response = await apiClient.get<PdfDownloadResponse>(
    `/portal/${slug}/prescriptions/${prescriptionId}/pdf`
  )
  return response.data.url
}

/**
 * Get distinct patients from the provider's prescription history.
 */
export async function getProviderPatients(
  slug: string,
  search?: string
): Promise<PatientListResponse> {
  const params = new URLSearchParams()
  if (search) params.set('search', search)
  const query = params.toString()
  const url = `/portal/${slug}/patients${query ? `?${query}` : ''}`
  const response = await apiClient.get<PatientListResponse>(url)
  return response.data
}

/**
 * Get full prescription details for prescribe-again flow.
 */
export async function getProviderPrescriptionDetail(
  slug: string,
  prescriptionId: string
): Promise<ProviderPrescriptionDetail> {
  const response = await apiClient.get<ProviderPrescriptionDetail>(
    `/portal/${slug}/prescriptions/${prescriptionId}`
  )
  return response.data
}

/* --------------------------------------------------------------------------
   Public Catalog & Access Requests (no auth required)
   -------------------------------------------------------------------------- */

/**
 * Get public catalog for the landing page.
 * No authentication required.
 */
export async function getPublicCatalog(
  slug: string
): Promise<PublicCatalogResponse> {
  const response = await apiClient.get<PublicCatalogResponse>(
    `/portal/${slug}/public-catalog`
  )
  return response.data
}

/**
 * Submit a provider access request.
 * No authentication required.
 */
export async function submitAccessRequest(
  slug: string,
  data: AccessRequestCreate
): Promise<{ message: string }> {
  const response = await apiClient.post<{ message: string }>(
    `/portal/${slug}/access-requests`,
    data
  )
  return response.data
}

/* --------------------------------------------------------------------------
   Exports
   -------------------------------------------------------------------------- */

export default {
  getPortalConfig,
  getCatalog,
  getRecentProducts,
  submitPrescription,
  submitPrescriptionBulk,
  clearCatalogCache,
  getProviderPrescriptions,
  getProviderPatients,
  getPrescriptionPdfUrl,
  getProviderPrescriptionDetail,
  getPublicCatalog,
  submitAccessRequest,
}
